import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  FiCalendar,
  FiClock,
  FiMapPin,
  FiPrinter,
  FiCheckCircle,
  FiFileText,
  FiLayers
} from "react-icons/fi";

const DetalleCitaContinuidad = ({
  cita,
  codigoAfiliado,
  apiUrlAgendador,
  apiKeyCitas = null,
  onVerHistorial = null,
  onAlerta = null,
}) => {
  const [imprimiendo, setImprimiendo] = useState(false);

  if (!cita) return null;

  const {
    folio,
    fecha_cita,
    hora_cita,
    estatus = "Agendada",
    tramite = "Entrega Cheques PMP",
    sede = "Sede Centro",
    sede_domicilio = "Magisterio 1155",
    puede_imprimir = true,
    cita_origen = null,
  } = cita;

  // Formatear fecha legible en español
  let fechaFormateada = fecha_cita;
  try {
    if (fecha_cita) {
      const [year, month, day] = fecha_cita.split("-").map(Number);
      const dateObj = new Date(year, month - 1, day);
      fechaFormateada = dateObj.toLocaleDateString("es-MX", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      fechaFormateada =
        fechaFormateada.charAt(0).toUpperCase() + fechaFormateada.slice(1);
    }
  } catch (e) {
    fechaFormateada = fecha_cita;
  }

  const handleDescargarPDF = async () => {
    if (!folio) {
      if (onAlerta) {
        onAlerta({
          tipo: "warning",
          mensaje: "No se encontró el folio de la cita para imprimir.",
        });
      }
      return;
    }

    setImprimiendo(true);
    try {
      const headers = { "Content-Type": "application/json" };
      if (apiKeyCitas) {
        headers["x-api-key"] = apiKeyCitas;
      }

      const response = await fetch(`${apiUrlAgendador}/GenerarPDF`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          codigo_afiliado: codigoAfiliado,
          folio_cita: String(folio),
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `Comprobante_Cita_${folio}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => {
          window.URL.revokeObjectURL(downloadUrl);
        }, 1000);

        if (onAlerta) {
          onAlerta({
            tipo: "success",
            mensaje: "Comprobante de cita descargado con éxito.",
          });
        }
      } else {
        const errText = await response.text();
        console.error("Error al descargar PDF:", errText);
        if (onAlerta) {
          onAlerta({
            tipo: "error",
            mensaje: "Error al generar el comprobante PDF.",
          });
        }
      }
    } catch (err) {
      console.error("Error al conectar con servidor de documentos:", err);
      if (onAlerta) {
        onAlerta({
          tipo: "error",
          mensaje: "Error de conexión al generar el comprobante.",
        });
      }
    } finally {
      setImprimiendo(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2 }}
      className="w-full bg-white flex flex-col"
    >
      {/* Header del Trámite y Cita */}
      <div className="p-5 md:px-8 md:py-6 border-b border-slate-200 bg-white flex flex-col lg:flex-row lg:items-center justify-between gap-5">
        <div className="flex items-start gap-4">
          <div className="size-11 rounded-xl bg-slate-50 border border-slate-200 text-emerald-600 flex items-center justify-center shrink-0">
            <FiCheckCircle className="size-5" />
          </div>
          <div>
            {/* Badges descansados y sutiles */}
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-xs font-medium text-slate-700">
                <span className="size-1.5 rounded-full bg-emerald-500 shrink-0" />
                <span>{estatus}</span>
              </div>

              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-xs font-medium text-slate-600">
                <FiLayers className="size-3 text-slate-500 shrink-0" />
                <span>Cita de Continuidad</span>
              </div>

              <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-xs font-mono text-slate-600">
                <span className="text-slate-400">Folio:</span>
                <span className="font-semibold text-slate-700">#{folio}</span>
              </div>
            </div>

            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
              {tramite}
            </h1>
            <p className="text-sm font-normal text-slate-500 mt-0.5">
              Tu trámite cuenta con una cita programada para continuar con tu proceso.
            </p>
          </div>
        </div>

        {/* Acciones Rápidas del Header */}
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-1.5 flex flex-col items-start lg:items-end">
            <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">
              Afiliado
            </span>
            <span className="text-xs font-mono font-semibold text-slate-700">
              #{codigoAfiliado}
            </span>
          </div>

          {onVerHistorial && (
            <button
              type="button"
              onClick={onVerHistorial}
              className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-xl bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-800 border border-slate-200 font-medium text-xs tracking-wide transition-colors cursor-pointer"
            >
              <FiFileText className="size-3.5 text-slate-400" />
              <span>Ver Historial</span>
            </button>
          )}

          {puede_imprimir && (
            <button
              type="button"
              onClick={handleDescargarPDF}
              disabled={imprimiendo}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-primary hover:bg-primary_light disabled:bg-slate-300 text-white font-semibold text-xs uppercase tracking-wider shadow-sm transition-all active:scale-95 cursor-pointer disabled:cursor-not-allowed"
            >
              {imprimiendo ? (
                <>
                  <div className="size-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Generando PDF...</span>
                </>
              ) : (
                <>
                  <FiPrinter className="size-4" />
                  <span>Imprimir Cita</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Contenido Principal a Ancho Completo */}
      <div className="p-5 md:p-8 flex flex-col gap-6 w-full">
        {/* Grid de 2 Columnas para Fecha/Hora y Sede */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 w-full">
          {/* Tarjeta de Fecha y Hora Programada */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 md:p-6 flex flex-col justify-between shadow-xs">
            <div className="flex items-center justify-between mb-4">
              <div className="size-10 rounded-xl bg-slate-50 border border-slate-200 text-slate-600 flex items-center justify-center">
                <FiCalendar className="size-5" />
              </div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 text-xs font-medium">
                <FiClock className="size-3 text-slate-400" />
                <span>{hora_cita} hrs</span>
              </div>
            </div>

            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider block mb-1">
                Fecha y hora programada
              </span>
              <h2 className="text-xl font-bold text-slate-900 leading-snug">
                {fechaFormateada}
              </h2>
              <p className="text-sm font-normal text-slate-600 mt-1.5">
                Horario de atención: <span className="font-semibold text-slate-800">{hora_cita} hrs</span>
              </p>
            </div>
          </div>

          {/* Tarjeta de Sede y Domicilio */}
          <div className="bg-white border border-slate-200 rounded-2xl p-5 md:p-6 flex flex-col justify-between shadow-xs">
            <div className="flex items-center justify-between mb-4">
              <div className="size-10 rounded-xl bg-slate-50 border border-slate-200 text-slate-600 flex items-center justify-center">
                <FiMapPin className="size-5" />
              </div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-xs font-medium text-slate-700">
                <span className="size-1.5 rounded-full bg-emerald-500 shrink-0" />
                <span>Sede asignada</span>
              </div>
            </div>

            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wider block mb-1">
                Lugar de presentación
              </span>
              <h2 className="text-xl font-bold text-slate-900 leading-snug">
                {sede}
              </h2>
              <p className="text-sm font-normal text-slate-600 mt-1.5">
                Dirección: <span className="font-semibold text-slate-800">{sede_domicilio}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Trámite Previo de Origen */}
        {cita_origen && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs w-full shadow-xs">
            <div className="flex items-center gap-3">
              <div className="size-9 rounded-xl bg-slate-50 border border-slate-200 text-slate-500 flex items-center justify-center shrink-0">
                <FiLayers className="size-4" />
              </div>
              <div>
                <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wider block">
                  Trámite previo de origen
                </span>
                <span className="text-sm font-semibold text-slate-800">
                  {cita_origen.tramite || "Préstamo de Automóvil"}
                </span>
                <span className="text-slate-500 font-mono ml-2">
                  (Folio: #{cita_origen.folio})
                </span>
              </div>
            </div>
            {cita_origen.estatus && (
              <div className="self-start sm:self-auto">
                <span className="px-2.5 py-1 bg-slate-50 border border-slate-200 text-slate-600 text-xs font-medium rounded-lg">
                  Estatus origen: <strong className="font-semibold text-slate-700">{cita_origen.estatus}</strong>
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default DetalleCitaContinuidad;
